/**
 * Money is always minor units plus an explicit currency.
 *
 * SRS §6.4 and §14: balances and totals are currency-specific, amounts never
 * cross currencies implicitly, and no amount is ever a bare number. The type
 * below makes the currency impossible to drop and `addMoney` refuses to mix
 * currencies at runtime rather than silently producing a wrong total.
 */

export type CurrencyCode = "USD" | "EUR" | "GBP";

/** Minor-unit exponent per currency. All three launch currencies are 2. */
const EXPONENT: Record<CurrencyCode, number> = { USD: 2, EUR: 2, GBP: 2 };

export interface Money {
  /** Integer minor units (cents). Never a float. */
  readonly minor: number;
  readonly currency: CurrencyCode;
}

export class CurrencyMismatchError extends Error {
  constructor(a: CurrencyCode, b: CurrencyCode) {
    super(`refusing to combine ${a} with ${b}: amounts never cross currencies`);
    this.name = "CurrencyMismatchError";
  }
}

export function money(minor: number, currency: CurrencyCode): Money {
  if (!Number.isInteger(minor)) {
    throw new TypeError(`money minor units must be an integer, got ${minor}`);
  }
  return { minor, currency };
}

/**
 * Parses the string the API sends for a minor-unit column.
 *
 * Amounts cross the wire as strings because the server holds them as bigint:
 * a Postgres `bigint` does not fit a JS number, and `fin.journal_lines`
 * genuinely can exceed 2^53 minor units in a low-denomination currency.
 * This side is display-only and `number` is enough for every amount a person
 * will read, so the conversion happens here — once, loudly, and refusing
 * anything it cannot represent exactly rather than rounding it into a total
 * that is off by a penny for no visible reason.
 */
export function fromMinorString(value: string | number, currency: CurrencyCode): Money {
  const s = String(value).trim();
  if (!/^-?\d+$/.test(s)) {
    throw new TypeError(`money must be an integer string, got ${JSON.stringify(value)}`);
  }
  const n = Number(s);
  if (!Number.isSafeInteger(n)) {
    throw new RangeError(`amount ${s} exceeds what this display can represent exactly`);
  }
  return { minor: n, currency };
}

export function addMoney(a: Money, b: Money): Money {
  if (a.currency !== b.currency) throw new CurrencyMismatchError(a.currency, b.currency);
  return { minor: a.minor + b.minor, currency: a.currency };
}

export function subMoney(a: Money, b: Money): Money {
  if (a.currency !== b.currency) throw new CurrencyMismatchError(a.currency, b.currency);
  return { minor: a.minor - b.minor, currency: a.currency };
}

/**
 * Sums a list that is already known to be one currency. An empty list has no
 * currency of its own, so the caller must supply one — returning a zero with a
 * guessed currency is exactly the bug this module exists to prevent.
 */
export function sumMoney(items: readonly Money[], currency: CurrencyCode): Money {
  return items.reduce<Money>((acc, m) => addMoney(acc, m), { minor: 0, currency });
}

export function isZero(m: Money): boolean {
  return m.minor === 0;
}

export function isNegative(m: Money): boolean {
  return m.minor < 0;
}

/**
 * Formats for display. Always renders the currency — there is no "bare number"
 * variant on purpose, because an unlabelled balance is the classic way a
 * two-currency ledger misleads someone.
 */
export function formatMoney(m: Money, locale = "en-US"): string {
  const exp = EXPONENT[m.currency];
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: m.currency,
    minimumFractionDigits: exp,
    maximumFractionDigits: exp,
  }).format(m.minor / 10 ** exp);
}

/** Parses user input ("12.34") into minor units. Returns null on anything invalid. */
export function parseMoney(input: string, currency: CurrencyCode): Money | null {
  const trimmed = input.trim().replace(/,/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
  const exp = EXPONENT[currency];
  const [whole = "0", frac = ""] = trimmed.split(".");
  if (frac.length > exp) return null; // more precision than the currency has
  const sign = whole.startsWith("-") ? -1 : 1;
  const minor =
    sign * (Math.abs(Number(whole)) * 10 ** exp + Number(frac.padEnd(exp, "0") || "0"));
  return Number.isSafeInteger(minor) ? { minor, currency } : null;
}
